/**
 * Email alerts — the ☰ menu's switch, Settings → Notifications, the plan-end banner and the
 * Invexis staff email log, all against /api/email-alerts.
 *
 * The SERVER remembers the choice: it is the one sending the emails, so a setting kept only
 * here could not stop them. This browser keeps a copy (localStorage) purely so the switch shows
 * the right position the instant the menu opens; the menu then asks the server and corrects
 * itself if the choice was changed on another device.
 *
 * Any change — from the menu or from Settings — is announced on one window event (and, through
 * localStorage, to other tabs), so both places move together the moment either one changes.
 */
import http from './httpClient';

export const EMAIL_ALERTS_EVENT = 'invexis:email-alerts-changed';
export const ALWAYS_ON_NOTE = 'Missed-TSR reminders, subscription reminders and account emails are always sent.';
export const MASTER_KEY = 'emailAlertsMaster';

/** The four emails the main switch governs, in menu order. Keys match backend/utils/notificationPrefs.js. */
export const EMAIL_TYPES = [
  { key: 'emailDailyPulse', label: 'Daily Pulse', hint: 'When the day\'s TSR is submitted' },
  { key: 'emailRadarAlerts', label: 'Radar', hint: 'When something on the TSR stands out' },
  { key: 'emailWeeklyReport', label: 'Weekly', hint: 'Mondays, last week vs the week before' },
  { key: 'emailMonthlyAudit', label: 'Monthly', hint: 'On the 1st, and when you close the month' },
];

const DEFAULTS = {
  isOwner: false,
  master: true,
  types: EMAIL_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: true }), {}),
};

function currentUserId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.userId || u.user_id || u.id || null;
  } catch (_) {
    return null;
  }
}

const cacheKey = () => `invexis:emailAlerts:${currentUserId() || 'me'}`;

function normalize(raw, base = DEFAULTS) {
  const r = raw || {};
  return {
    isOwner: typeof r.isOwner === 'boolean' ? r.isOwner : base.isOwner,
    master: typeof r.master === 'boolean' ? r.master : base.master,
    types: { ...base.types, ...(r.types || {}) },
  };
}

/** The last known switches for this person, or null — never throws. */
export function readCachedEmailAlerts() {
  try {
    const raw = localStorage.getItem(cacheKey());
    return raw ? normalize(JSON.parse(raw)) : null;
  } catch (_) {
    return null;
  }
}

function writeCache(prefs) {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(prefs));
  } catch (_) { /* a private window: the server still has it */ }
}

function announce(prefs) {
  try {
    window.dispatchEvent(new CustomEvent(EMAIL_ALERTS_EVENT, { detail: prefs }));
  } catch (_) { /* no window (tests) */ }
}

/** Ask the server, refresh the copy, tell every listener. */
export async function fetchEmailAlerts() {
  const res = await http.get('/email-alerts/preferences');
  if (!res || res.success === false) throw new Error((res && res.error) || 'Could not load your email settings');
  const prefs = normalize(res);
  writeCache(prefs);
  announce(prefs);
  return prefs;
}

/**
 * Save a change: { master: false } or { types: { emailRadarAlerts: false } }. Throws on failure
 * so the caller can move the switch back and say "Could not save, try again".
 */
export async function saveEmailAlerts(change) {
  const res = await http.put('/email-alerts/preferences', change);
  if (!res || res.success === false) throw new Error((res && res.error) || 'Could not save, try again');
  const prefs = normalize(res, readCachedEmailAlerts() || DEFAULTS);
  writeCache(prefs);
  announce(prefs);
  return prefs;
}

/**
 * Settings → Notifications saves through its own endpoint; it calls this afterwards so the
 * menu follows at once. `partial` is { emailAlertsMaster?: bool, emailDailyPulse?: bool, ... }.
 */
export function announceEmailAlertsChange(partial) {
  const cur = readCachedEmailAlerts() || DEFAULTS;
  const next = { ...cur, types: { ...cur.types } };
  Object.entries(partial || {}).forEach(([key, value]) => {
    if (key === MASTER_KEY) next.master = !!value;
    else if (key in next.types) next.types[key] = !!value;
  });
  writeCache(next);
  announce(next);
  return next;
}

/** Subscribe to changes from this tab and from other tabs. Returns the unsubscribe function. */
export function onEmailAlertsChanged(listener) {
  const onEvent = (e) => listener(normalize(e.detail));
  const onStorage = (e) => {
    if (e.key !== cacheKey() || !e.newValue) return;
    try { listener(normalize(JSON.parse(e.newValue))); } catch (_) { /* ignore */ }
  };
  window.addEventListener(EMAIL_ALERTS_EVENT, onEvent);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EMAIL_ALERTS_EVENT, onEvent);
    window.removeEventListener('storage', onStorage);
  };
}

/** The business's plan end and days left, for the banner. Never throws. */
export async function fetchSubscriptionStatus() {
  try {
    const res = await http.get('/email-alerts/subscription-status');
    return res && res.success !== false ? res : null;
  } catch (_) {
    return null;
  }
}

/** Invexis staff: the last 100 emails of a business. */
export async function fetchEmailLog(businessId) {
  return http.get(`/email-alerts/admin/log?businessId=${encodeURIComponent(businessId)}&limit=100`);
}

/** Invexis staff: build an email from real data without sending it. */
export async function fetchEmailPreview(params) {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  return http.get(`/email-alerts/admin/preview?${q.toString()}`);
}
